import {
  HttpClient,
  robotoHeaders,
  type PaginatedResponse,
} from "@/shared/http";

import {
  type CreateIngestionRuleRequest,
  type DefaultIngestionRule,
  type IngestionRuleRecord,
  type UpdateIngestionRuleRequest,
} from "./IngestionRuleRecord";
import {
  MAX_INGEST_FILES,
  type ExistingFilesPreview,
  type IngestFileResult,
  type IngestionRun,
} from "./IngestionRun";

interface Options {
  abortSignal?: AbortSignal;
}

/** The most paths one `matchRules` call may ask about; the server's cap. */
export const MAX_MATCH_PATHS = 200;

/** CRUD over an org's ingestion rules (`/v1/ingestion-rules`). */
export class IngestionRuleService {
  #httpClient: HttpClient;

  constructor(httpClient: HttpClient) {
    this.#httpClient = httpClient;
  }

  public async listRules(
    orgId: string,
    options?: Options,
  ): Promise<IngestionRuleRecord[]> {
    const requestUrl = this.#httpClient.constructUrl("v1/ingestion-rules");
    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId }),
    });
    const page = await response.json<PaginatedResponse<IngestionRuleRecord>>();
    return page.items;
  }

  public async getRule(
    ruleId: string,
    orgId: string,
    options?: Options,
  ): Promise<IngestionRuleRecord> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/ingestion-rules/${encodeURIComponent(ruleId)}`,
    );
    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId }),
    });
    return response.json<IngestionRuleRecord>();
  }

  public async createRule(
    request: CreateIngestionRuleRequest,
    orgId: string,
    options?: Options,
  ): Promise<IngestionRuleRecord> {
    const requestUrl = this.#httpClient.constructUrl("v1/ingestion-rules");
    const response = await this.#httpClient.post(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId }),
      body: JSON.stringify(request),
    });
    return response.json<IngestionRuleRecord>();
  }

  public async updateRule(
    ruleId: string,
    updates: UpdateIngestionRuleRequest,
    orgId: string,
    options?: Options,
  ): Promise<IngestionRuleRecord> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/ingestion-rules/${encodeURIComponent(ruleId)}`,
    );
    const response = await this.#httpClient.put(requestUrl, {
      idempotent: true,
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId }),
      body: JSON.stringify(updates),
    });
    return response.json<IngestionRuleRecord>();
  }

  public async deleteRule(
    ruleId: string,
    orgId: string,
    options?: Options,
  ): Promise<void> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/ingestion-rules/${encodeURIComponent(ruleId)}`,
    );
    const response = await this.#httpClient.delete(requestUrl, {
      idempotent: true,
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId }),
    });
    return response.throwIfError();
  }

  /** The rules Roboto offers every org, and whether the org has each already. */
  public async listDefaultRules(
    orgId: string,
    options?: Options,
  ): Promise<DefaultIngestionRule[]> {
    const requestUrl = this.#httpClient.constructUrl(
      "v1/ingestion-rules/defaults",
    );
    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId }),
    });
    const body = await response.json<{ items: DefaultIngestionRule[] }>();
    return body.items;
  }

  /** Creates whichever of the default rules the org is missing; returns them. */
  public async createDefaultRules(
    orgId: string,
    options?: Options,
  ): Promise<IngestionRuleRecord[]> {
    const requestUrl = this.#httpClient.constructUrl(
      "v1/ingestion-rules/defaults",
    );
    const response = await this.#httpClient.post(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId }),
    });
    const body = await response.json<{ items: IngestionRuleRecord[] }>();
    return body.items;
  }

  /**
   * The org's rules that match each path, decided by the same matcher that
   * marks a file ingestable. A path no rule matches maps to an empty list.
   */
  public async matchRules(
    paths: readonly string[],
    orgId: string,
    options?: Options,
  ): Promise<Record<string, IngestionRuleRecord[]>> {
    if (paths.length > MAX_MATCH_PATHS) {
      throw new Error(
        `Ask about at most ${MAX_MATCH_PATHS} paths at a time; got ${paths.length}.`,
      );
    }
    const requestUrl = this.#httpClient.constructUrl(
      "v1/ingestion-rules/match",
    );
    const response = await this.#httpClient.post(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId }),
      body: JSON.stringify({ paths }),
    });
    const body = await response.json<{
      matches: Record<string, IngestionRuleRecord[]>;
    }>();
    return body.matches;
  }

  /**
   * Ingest existing files now with the org's rules that match them: each
   * file is marked ingestable and the matching rule's trigger runs on it, as
   * if it had just been uploaded. Also how a failed ingestion is run again.
   */
  public async ingestFiles(
    fileIds: readonly string[],
    orgId: string,
    options?: Options,
  ): Promise<IngestFileResult[]> {
    if (fileIds.length > MAX_INGEST_FILES) {
      throw new Error(
        `Ingest at most ${MAX_INGEST_FILES} files at a time; got ${fileIds.length}.`,
      );
    }
    const requestUrl = this.#httpClient.constructUrl(
      "v1/ingestion-rules/ingest",
    );
    const response = await this.#httpClient.post(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId }),
      body: JSON.stringify({ file_ids: fileIds }),
    });
    const body = await response.json<{ results: IngestFileResult[] }>();
    return body.results;
  }

  /**
   * The latest run of an ingestion rule's trigger on each file. A file no
   * rule's trigger has run on is absent from the result.
   */
  public async latestRuns(
    fileIds: readonly string[],
    orgId: string,
    options?: Options,
  ): Promise<Record<string, IngestionRun>> {
    if (fileIds.length > MAX_MATCH_PATHS) {
      throw new Error(
        `Ask about at most ${MAX_MATCH_PATHS} files at a time; got ${fileIds.length}.`,
      );
    }
    const requestUrl = this.#httpClient.constructUrl(
      "v1/ingestion-rules/runs/latest",
    );
    const response = await this.#httpClient.post(requestUrl, {
      idempotent: true,
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId }),
      body: JSON.stringify({ file_ids: fileIds }),
    });
    const body = await response.json<{ runs: Record<string, IngestionRun> }>();
    return body.runs;
  }

  /** Files already uploaded that the rule matches and its trigger never ran on. */
  public async previewExistingFiles(
    ruleId: string,
    orgId: string,
    options?: Options,
  ): Promise<ExistingFilesPreview> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/ingestion-rules/${encodeURIComponent(ruleId)}/existing-files`,
    );
    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId }),
    });
    return response.json<ExistingFilesPreview>();
  }

  /**
   * Queue ingestion of the files `previewExistingFiles` counts. Roboto works
   * through them in the background; the runs queue like any other actions.
   */
  public async ingestExistingFiles(
    ruleId: string,
    orgId: string,
    options?: Options,
  ): Promise<ExistingFilesPreview> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/ingestion-rules/${encodeURIComponent(ruleId)}/existing-files/ingest`,
    );
    const response = await this.#httpClient.post(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId }),
    });
    return response.json<ExistingFilesPreview>();
  }
}
